<div class="fee-group-page">

  @if (!group) {
    <div class="loading-state">
      <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
    </div>
  } @else {

    <div class="group-header">
      <div class="group-title">
        <h2 class="group-name">{{ group.name }}</h2>
        <button class="icon-btn" type="button" (click)="openEditGroupName()" [title]="'fee.edit_group_name_action' | translate">
          <mat-icon>edit</mat-icon>
        </button>
      </div>
      <div class="group-header-actions desktop-only">
        <button class="btn-delete-group" type="button" (click)="openDeleteGroup()">
          <span class="delete-group-icon" aria-hidden="true"></span>
          {{ 'fee.delete_group' | translate }}
        </button>
      </div>
    </div>

    <div class="subgroup-tabs-panel">
      <div class="tab-bar">
        <div class="tab-bar-scroll">
          <button class="slide-btn"
                  type="button"
                  (click)="scrollLeft()"
                  [disabled]="!canScrollLeft"
                  [class.hidden]="!canScrollLeft && !canScrollRight">
            <mat-icon>chevron_left</mat-icon>
          </button>

          <button class="tab-btn"
                  type="button"
                  [class.active]="activeSubgroupIndex === -1"
                  (click)="selectAllMembers()">
            {{ 'fee.all_members' | translate }}
            <span class="tab-count">({{ totalMembersCount }})</span>
          </button>

          @for (item of visibleSubgroupTabs; track item.sg.id) {
            <button class="tab-btn"
                    type="button"
                    [class.active]="activeSubgroupIndex === item.idx"
                    (click)="selectSubgroup(item.idx)">
              {{ item.sg.name }}
            </button>
          }

          <button class="slide-btn"
                  type="button"
                  (click)="scrollRight()"
                  [disabled]="!canScrollRight"
                  [class.hidden]="!canScrollLeft && !canScrollRight">
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>

        <div class="tab-bar-actions desktop-only">
          @if (activeSubgroup) {
            <button class="btn-delete-subgroup" type="button" (click)="openDeleteSubgroup()">
              <span class="delete-group-icon" aria-hidden="true"></span>
              {{ 'fee.delete_subgroup' | translate }}
            </button>
          }
          <button class="btn-add-subgroup" type="button" (click)="openAddSubgroup()">
            <mat-icon>add</mat-icon>
            {{ 'fee.actions.add_subgroup' | translate }}
          </button>
        </div>
      </div>
    </div>

    @if (activeSubgroup) {
      <div class="subgroup-meta">
        <div class="meta-grid">
          <div class="meta-item">
            <span class="meta-label">{{ 'fee.mentor_name' | translate }}</span>
            <span class="meta-value">{{ activeSubgroup.mentorName || ('common.unknown' | translate) }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">{{ 'fee.start_date_end_date' | translate }}</span>
            <span class="meta-value">{{ formatDateRange(activeSubgroup) }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">{{ 'fee.frequency' | translate }}</span>
            <span class="meta-value">{{ frequencyLabel(activeSubgroup.frequency) }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">{{ 'fee.payment_flow' | translate }}</span>
            <span class="meta-value">{{ paymentFlowLabel(activeSubgroup.paymentFlow) }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">{{ 'fee.price' | translate }}</span>
            <span class="meta-value">
              {{ activeSubgroup.price }} ֏
            </span>
          </div>
          <div class="meta-item">
            <span class="meta-label">{{ 'fee.discount' | translate }}</span>
            <span class="meta-value">
              @if (activeSubgroup.discountValue) {
                {{ activeSubgroup.discountValue }}{{ activeSubgroup.discountType === 'PERCENT' ? '%' : ' ֏' }}
              } @else {
                —
              }
            </span>
          </div>
        </div>
        <div class="meta-actions">
          <button class="btn-edit-subgroup-icon" type="button" (click)="openEditSubgroup()" [title]="'fee.edit_subgroup_action' | translate">
            <mat-icon>edit</mat-icon>
          </button>
        </div>
      </div>
    }

    <div class="tab-content">
      <div class="members-toolbar">
        @if (activeSubgroup) {
          <button class="btn-add-member-tab desktop-only" type="button" (click)="openAddMember(true)">
            <mat-icon>add</mat-icon>
            {{ 'fee.actions.add_member' | translate }}
          </button>
        }
        <div class="members-tools">
          <div class="search-field">
            <mat-icon>search</mat-icon>
            <input
              type="text"
              [placeholder]="'fee.search_transaction' | translate"
              [value]="searchTerm"
              (input)="onSearchTermChange($any($event.target).value)"
            />
          </div>
          <button class="tool-icon-btn" type="button" [title]="'fee.filter' | translate">
            <mat-icon>filter_list</mat-icon>
            <span class="filter-label">{{ 'fee.filter' | translate }}</span>
          </button>
        </div>
      </div>

      @if (isMembersLoading) {
        <div class="loading-state">
          <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
        </div>
      } @else if (filteredMemberRows.length > 0) {
        <div class="members-table-wrap">
          <table class="members-table desktop-table">
            <thead>
            <tr>
              <th>{{ 'fee.member_name' | translate }}</th>
              <th>{{ 'fee.frequency' | translate }}</th>
              <th>{{ 'fee.discount' | translate }}</th>
              <th>{{ 'fee.due' | translate }}</th>
              <th>{{ 'fee.total' | translate }}</th>
              <th>{{ 'fee.payment_status' | translate }}</th>
              <th>{{ 'fee.deactivate' | translate }}</th>
              <th class="col-actions"></th>
            </tr>
            </thead>
            <tbody>
              @for (member of displayedMemberRows; track member.id + '-' + member.subgroupId) {
                <tr [class.row-inactive]="member.active === false">
                  <td class="member-name-link" (click)="openMemberProfile(member.id, member.subgroupId)">{{ member.name }}</td>
                  <td>{{ member.frequency }}</td>
                  <td>{{ member.discount }}</td>
                  <td>{{ member.due | number }} {{ currencySymbol }}</td>
                  <td>{{ member.total | number }} {{ currencySymbol }}</td>
                  <td>
                    <span class="status-badge"
                          [class.status-paid]="member.paymentStatus === 'PAID'"
                          [class.status-pending]="member.paymentStatus === 'PENDING'">
                      @if (member.paymentStatus === 'PAID') {
                        <mat-icon class="status-icon">check_circle</mat-icon> {{ 'fee.paid' | translate }}
                      } @else if (member.paymentStatus === 'PENDING') {
                        <mat-icon class="status-icon status-icon-pending">fiber_manual_record</mat-icon> {{ 'fee.status.pending' | translate }}
                      } @else {
                        {{ member.paymentStatus | titlecase }}
                      }
                    </span>
                  </td>
                  <td>
                    <label class="toggle">
                      <input
                        type="checkbox"
                        [checked]="member.active ?? true"
                        (change)="toggleMemberActive(member, $event)">
                      <span class="toggle-ui"></span>
                    </label>
                  </td>
                  <td class="col-actions member-row-actions" (click)="$event.stopPropagation()">
                    <button class="row-action-btn"
                            type="button"
                            [disabled]="member.active === false"
                            [title]="'fee.more_actions' | translate"
                            (click)="toggleMemberMenu(member, $event)">
                      <mat-icon>more_vert</mat-icon>
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <div class="members-mobile-cards">
          @for (member of displayedMemberRows; track member.id + '-' + member.subgroupId) {
            <div class="member-mobile-card" [class.member-mobile-card--inactive]="member.active === false">
              <div class="mm-top-row">
                <div class="mm-name-col">
                  <span class="mm-label">{{ 'fee.member_name' | translate }}</span>
                  <span class="mm-name"
                        (click)="openMemberProfile(member.id, member.subgroupId)">{{ member.name }}</span>
                </div>
                <span class="status-badge"
                      [class.status-paid]="member.paymentStatus === 'PAID'"
                      [class.status-pending]="member.paymentStatus === 'PENDING'">
                  @if (member.paymentStatus === 'PAID') {
                    {{ 'fee.paid' | translate }} <mat-icon class="status-icon">check_circle</mat-icon>
                  } @else if (member.paymentStatus === 'PENDING') {
                    {{ 'fee.status.pending' | translate }} <mat-icon class="status-icon status-icon-pending">fiber_manual_record</mat-icon>
                  } @else {
                    {{ member.paymentStatus | titlecase }}
                  }
                </span>
              </div>

              <div class="mm-field">
                <span class="mm-label">{{ 'fee.frequency' | translate }}</span>
                <span class="mm-value">{{ member.frequency }}</span>
              </div>
              <div class="mm-field">
                <span class="mm-label">{{ 'fee.discount' | translate }}</span>
                <span class="mm-value">{{ member.discount }}</span>
              </div>
              <div class="mm-field">
                <span class="mm-label">{{ 'fee.due' | translate }}</span>
                <span class="mm-value">{{ member.due | number }} {{ currencySymbol }}</span>
              </div>
              <div class="mm-bottom-row">
                <div class="mm-field">
                  <span class="mm-label">{{ 'fee.total' | translate }}</span>
                  <span class="mm-value">{{ member.total | number }} {{ currencySymbol }}</span>
                </div>
                <div class="mm-actions">
                  <label class="toggle">
                    <input
                      type="checkbox"
                      [checked]="member.active ?? true"
                      (change)="toggleMemberActive(member, $event)">
                    <span class="toggle-ui"></span>
                  </label>
                  <div class="member-row-actions">
                    <button class="row-action-btn"
                            type="button"
                            [disabled]="member.active === false"
                            [title]="'fee.more_actions' | translate"
                            (click)="toggleMemberMenu(member, $event)">
                      <mat-icon>more_vert</mat-icon>
                    </button>
                    @if (openedMemberMenuKey === memberRowKey(member) && member.active !== false) {
                      <div class="more-menu more-menu--mobile" (click)="$event.stopPropagation()">
                        <button type="button" class="more-menu-item" (click)="openEditMemberInfo(member)">
                          <span>{{ 'fee.edit_member_info' | translate }}</span>
                          <mat-icon>edit</mat-icon>
                        </button>
                        <button type="button"
                                class="more-menu-item"
                                [disabled]="sendingNotificationMemberKey === memberRowKey(member)"
                                (click)="sendMemberNotification(member)">
                          <span>{{ 'fee.send_notification' | translate }}</span>
                          <mat-icon>notifications_none</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
        @if (hasMoreMemberRows) {
          <div class="fee-load-more-row d-flex justify-content-center mt-4 flex-direction-row gap-10px">
            @if (!isLoading) {
              <button class="load-history-button" type="button" (click)="loadMore()">{{ 'my_team.load_more' | translate }}</button>
              <div class="search-loader">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            }
          </div>
        }
      } @else {
        <div class="empty-state">
          <svg class="empty-icon" width="48" height="48" viewBox="0 0 24 24" fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#CBD5E1" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="9" cy="7" r="4" stroke="#CBD5E1" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <p class="empty-text">
            {{ (searchTerm ? 'fee.no_results_found' : (activeSubgroupIndex === -1 ? 'fee.no_members_yet' : 'fee.no_members_in_subgroup')) | translate }}
          </p>
        </div>
      }
    </div>

    @if (openedMemberMenuKey && memberMenuPosition && activeMenuMember) {
      <div class="more-menu more-menu--fixed"
           [style.top.px]="memberMenuPosition.top"
           [style.left.px]="memberMenuPosition.left"
           (click)="$event.stopPropagation()">
        <button type="button" class="more-menu-item" (click)="openEditMemberInfo(activeMenuMember)">
          <span>{{ 'fee.edit_member_info' | translate }}</span>
          <mat-icon>edit</mat-icon>
        </button>
        <button type="button"
                class="more-menu-item"
                [disabled]="sendingNotificationMemberKey === memberRowKey(activeMenuMember)"
                (click)="sendMemberNotification(activeMenuMember)">
          <span>{{ 'fee.send_notification' | translate }}</span>
          <mat-icon>notifications_none</mat-icon>
        </button>
      </div>
    }

    <div class="common-footer fee-group-mobile-footer">
      <button class="mobile-add-subgroup" type="button" (click)="openAddSubgroup()">
        <mat-icon>add</mat-icon>
        {{ 'fee.actions.add_subgroup' | translate }}
      </button>
      <button class="mobile-delete-group" type="button" (click)="openDeleteGroup()">
        <span class="delete-group-icon" aria-hidden="true"></span>
        {{ 'fee.delete_group' | translate }}
      </button>
    </div>

  }
</div>
